import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Table, Button, Modal, Form, Input, InputNumber, Popconfirm, Space, Divider } from 'antd'
import { EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { useSizeGroupList, useCreateSizeGroup, useUpdateSizeGroup, useDeleteSizeGroup, useSizeGroup, useAddSizeToGroup, useUpdateSizeInGroup, useDeleteSizeFromGroup } from 'store/server/useSizeGroup'
import MainTitle from 'components/MainTitle'
import Spinner from 'components/spinner'
import { toast } from 'components/ui/use-toast'

const { TextArea } = Input

export default function SizeGroupManagement() {
  const { brand } = useParams()
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState('')
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [sizeModalVisible, setSizeModalVisible] = useState(false)
  const [selectedSizeGroup, setSelectedSizeGroup] = useState(null)
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(null)
  const [isEditingSize, setIsEditingSize] = useState(false)
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()
  const [sizeForm] = Form.useForm()

  // Brand name'i normalize et (tire'den boşluğa çevir)
  const normalizedBrand = brand?.replace(/-/g, ' ').toLowerCase() || 'zarahome'
  const brandForApi = normalizedBrand === 'zara home' ? 'zarahome' : normalizedBrand

  const { data: sizeGroups = [], isLoading } = useSizeGroupList(brandForApi)
  const { data: sizeGroupDetail, refetch: refetchSizeGroupDetail } = useSizeGroup(
    selectedSizeGroup?.id,
    brandForApi
  )
  const createMutation = useCreateSizeGroup()
  const updateMutation = useUpdateSizeGroup()
  const deleteMutation = useDeleteSizeGroup()
  const addSizeMutation = useAddSizeToGroup()
  const updateSizeMutation = useUpdateSizeInGroup()
  const deleteSizeMutation = useDeleteSizeFromGroup()

  // Arama filtresi
  const filteredData = sizeGroups.filter((item) => {
    if (!searchText) return true
    const searchLower = searchText.toLowerCase()
    return (
      item.name?.toLowerCase().includes(searchLower) ||
      item.id?.toString().includes(searchLower) ||
      item.variant?.toLowerCase().includes(searchLower)
    )
  })

  const handleCreate = () => {
    form.resetFields()
    setSelectedSizeGroup(null)
    setCreateModalVisible(true)
  }

  const handleCreateSubmit = async (values) => {
    try {
      await createMutation.mutateAsync({
        ...values,
        brand: brandForApi,
        sizes: values.sizes ? JSON.parse(values.sizes) : []
      })
      setCreateModalVisible(false)
      form.resetFields()
    } catch (error) {
      // Error toast is already handled in mutation
    }
  }

  const handleEdit = (record) => {
    setSelectedSizeGroup(record)
    editForm.setFieldsValue({
      name: record.name,
      variant: record.variant || 'default',
      departmentId: record.departmentId,
      sizes: JSON.stringify(record.sizes || [], null, 2)
    })
    setEditModalVisible(true)
  }

  const handleEditSubmit = async (values) => {
    try {
      await updateMutation.mutateAsync({
        id: selectedSizeGroup.id,
        body: {
          ...values,
          brand: brandForApi,
          sizes: values.sizes ? JSON.parse(values.sizes) : []
        }
      })
      setEditModalVisible(false)
      setSelectedSizeGroup(null)
      editForm.resetFields()
    } catch (error) {
      // Error toast is already handled in mutation
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteMutation.mutateAsync({ id, brand: brandForApi })
    } catch (error) {
      // Error toast is already handled in mutation
    }
  }

  const handleViewDetail = (record) => {
    setSelectedSizeGroup(record)
    setDetailModalVisible(true)
  }

  const handleAddSize = () => {
    sizeForm.resetFields()
    setIsEditingSize(false)
    setSelectedSizeIndex(null)
    setSizeModalVisible(true)
  }

  const handleEditSize = (sizeIndex) => {
    if (!sizeGroupDetail?.sizes || !Array.isArray(sizeGroupDetail.sizes)) return
    const size = sizeGroupDetail.sizes[sizeIndex]
    setSelectedSizeIndex(sizeIndex)
    setIsEditingSize(true)
    sizeForm.setFieldsValue(size)
    setSizeModalVisible(true)
  }

  const handleSizeSubmit = async (values) => {
    if (!selectedSizeGroup) return
    
    try {
      if (isEditingSize && selectedSizeIndex !== null) {
        await updateSizeMutation.mutateAsync({
          id: selectedSizeGroup.id,
          sizeIndex: selectedSizeIndex,
          body: {
            ...values,
            brand: brandForApi
          }
        })
      } else {
        await addSizeMutation.mutateAsync({
          id: selectedSizeGroup.id,
          body: {
            size: values,
            brand: brandForApi
          }
        })
      }
      setSizeModalVisible(false)
      sizeForm.resetFields()
      setIsEditingSize(false)
      setSelectedSizeIndex(null)
      refetchSizeGroupDetail()
    } catch (error) {
      // Error toast is already handled in mutation
    }
  }

  const handleDeleteSize = async (sizeIndex) => {
    if (!selectedSizeGroup) return
    
    try {
      await deleteSizeMutation.mutateAsync({
        id: selectedSizeGroup.id,
        sizeIndex,
        brand: brandForApi
      })
      refetchSizeGroupDetail()
    } catch (error) {
      // Error toast is already handled in mutation
    }
  }

  // SizeGroup detayı değiştiğinde yeniden fetch et
  useEffect(() => {
    if (detailModalVisible && selectedSizeGroup?.id) {
      refetchSizeGroupDetail()
    }
  }, [detailModalVisible, selectedSizeGroup?.id, refetchSizeGroupDetail])

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id) => <span className="font-mono text-xs">{String(id).substring(0, 8)}...</span>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 300,
      render: (text) => <span className="font-semibold">{text || '-'}</span>
    },
    {
      title: 'Size Count',
      key: 'sizeCount',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <span className="text-blue-600 font-medium">
          {Array.isArray(record.sizes) ? record.sizes.length : 0}
        </span>
      )
    },
    {
      title: 'Variant',
      dataIndex: 'variant',
      key: 'variant',
      width: 150,
      render: (text) => <span>{text || 'default'}</span>
    },
    {
      title: 'Department',
      key: 'department',
      width: 200,
      render: (_, record) => (
        <span>{record.Department?.name || '-'}</span>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            Detay
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="SizeGroup'u silmek istediğinize emin misiniz?"
            description="Bu işlem geri alınamaz."
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  if (isLoading) {
    return <Spinner className="mt-20" />
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/güncellemeler')}
          >
            Geri Dön
          </Button>
          <MainTitle>
            {brandForApi === 'zarahome' ? 'ZaraHome' : brandForApi === 'zara' ? 'Zara' : brandForApi} - SizeGroup Yönetimi
          </MainTitle>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Yeni SizeGroup Ekle
        </Button>
      </div>

      {/* Arama */}
      <div className="mb-4">
        <Input
          placeholder="SizeGroup ara (ID, Name, Variant)..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ maxWidth: 400 }}
        />
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Toplam ${total} kayıt`
        }}
        scroll={{ x: 1400 }}
      />

      {/* Create Modal */}
      <Modal
        title="Yeni SizeGroup Ekle"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        okText="Oluştur"
        cancelText="İptal"
        width={1200}
        confirmLoading={createMutation.isPending}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name zorunludur' }]}
          >
            <Input placeholder="SizeGroup adı" />
          </Form.Item>
          <Form.Item
            name="variant"
            label="Variant"
            initialValue="default"
          >
            <Input placeholder="Variant (varsayılan: default)" />
          </Form.Item>
          <Form.Item
            name="departmentId"
            label="Department ID"
          >
            <InputNumber
              placeholder="Department ID (opsiyonel)"
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>
          <Form.Item
            name="sizes"
            label="Sizes (JSON)"
            tooltip={`Size array'ini JSON formatında girin. Örnek: [{"scode":"S","cm":"90"}]`}
          >
            <TextArea
              rows={8}
              placeholder='[{"scode":"S","cm":"90","inches":"35","size":"S","gender":"M","europe":"S"}]'
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="SizeGroup Düzenle"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          setSelectedSizeGroup(null)
          editForm.resetFields()
        }}
        onOk={() => editForm.submit()}
        okText="Güncelle"
        cancelText="İptal"
        width={1200}
        confirmLoading={updateMutation.isPending}
        style={{ top: 20 }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name zorunludur' }]}
          >
            <Input placeholder="SizeGroup adı" />
          </Form.Item>
          <Form.Item
            name="variant"
            label="Variant"
          >
            <Input placeholder="Variant" />
          </Form.Item>
          <Form.Item
            name="departmentId"
            label="Department ID"
          >
            <InputNumber
              placeholder="Department ID (opsiyonel)"
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>
          <Form.Item
            name="sizes"
            label="Sizes (JSON)"
            tooltip="Size array'ini JSON formatında girin"
          >
            <TextArea
              rows={8}
              placeholder="Size array JSON formatında"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`SizeGroup Detayı - ${sizeGroupDetail?.name || selectedSizeGroup?.name || ''}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false)
          setSelectedSizeGroup(null)
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false)
            setSelectedSizeGroup(null)
          }}>
            Kapat
          </Button>
        ]}
        width="95%"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        {sizeGroupDetail && (
          <div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p><strong>ID:</strong> {sizeGroupDetail.id}</p>
                <p><strong>Name:</strong> {sizeGroupDetail.name}</p>
                <p><strong>Variant:</strong> {sizeGroupDetail.variant || 'default'}</p>
              </div>
              <div>
                <p><strong>Department:</strong> {sizeGroupDetail.Department?.name || '-'}</p>
                <p><strong>Size Count:</strong> {Array.isArray(sizeGroupDetail.sizes) ? sizeGroupDetail.sizes.length : 0}</p>
              </div>
            </div>
            
            <Divider />
            
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Sizes</h3>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddSize}
              >
                Yeni Size Ekle
              </Button>
            </div>

            {Array.isArray(sizeGroupDetail.sizes) && sizeGroupDetail.sizes.length > 0 ? (
              <Table
                dataSource={sizeGroupDetail.sizes.map((size, index) => ({ ...size, index }))}
                rowKey="index"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1400 }}
                size="small"
                columns={[
                  {
                    title: '#',
                    dataIndex: 'index',
                    key: 'index',
                    width: 50,
                    render: (index) => index + 1
                  },
                  {
                    title: 'S. Code',
                    dataIndex: 'scode',
                    key: 'scode',
                    width: 120
                  },
                  {
                    title: 'CM',
                    dataIndex: 'cm',
                    key: 'cm',
                    width: 100
                  },
                  {
                    title: 'Inches',
                    dataIndex: 'inches',
                    key: 'inches',
                    width: 100
                  },
                  {
                    title: 'Size',
                    dataIndex: 'size',
                    key: 'size',
                    width: 100
                  },
                  {
                    title: 'Gender',
                    dataIndex: 'gender',
                    key: 'gender',
                    width: 120
                  },
                  {
                    title: 'Europe',
                    dataIndex: 'europe',
                    key: 'europe',
                    width: 120
                  },
                  {
                    title: 'USA',
                    dataIndex: 'usa',
                    key: 'usa',
                    width: 100
                  },
                  {
                    title: 'Mexico',
                    dataIndex: 'mexico',
                    key: 'mexico',
                    width: 100
                  },
                  {
                    title: 'BR-Brasil',
                    dataIndex: 'br-brasil',
                    key: 'br-brasil',
                    width: 120
                  },
                  {
                    title: 'CN',
                    dataIndex: 'cn',
                    key: 'cn',
                    width: 100
                  },
                  {
                    title: 'Lower CN',
                    dataIndex: 'lowercn',
                    key: 'lowercn',
                    width: 120
                  },
                  {
                    title: 'CN Upper Woman',
                    dataIndex: 'cnupperwoman',
                    key: 'cnupperwoman',
                    width: 150
                  },
                  {
                    title: 'CN Lower Woman',
                    dataIndex: 'cnlowerwoman',
                    key: 'cnlowerwoman',
                    width: 150
                  },
                  {
                    title: 'İşlemler',
                    key: 'actions',
                    width: 150,
                    fixed: 'right',
                    render: (_, record) => (
                      <Space size="small">
                        <Button
                          type="default"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => handleEditSize(record.index)}
                        >
                          Düzenle
                        </Button>
                        <Popconfirm
                          title="Size'ı silmek istediğinize emin misiniz?"
                          description="Bu işlem geri alınamaz."
                          onConfirm={() => handleDeleteSize(record.index)}
                          okText="Evet"
                          cancelText="Hayır"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                          >
                            Sil
                          </Button>
                        </Popconfirm>
                      </Space>
                    )
                  }
                ]}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                Henüz size eklenmemiş. Yeni size eklemek için butona tıklayın.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Size Add/Edit Modal */}
      <Modal
        title={isEditingSize ? 'Size Düzenle' : 'Yeni Size Ekle'}
        open={sizeModalVisible}
        onCancel={() => {
          setSizeModalVisible(false)
          sizeForm.resetFields()
          setIsEditingSize(false)
          setSelectedSizeIndex(null)
        }}
        onOk={() => sizeForm.submit()}
        okText={isEditingSize ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        width={1000}
        confirmLoading={isEditingSize ? updateSizeMutation.isPending : addSizeMutation.isPending}
        style={{ top: 20 }}
      >
        <Form
          form={sizeForm}
          layout="vertical"
          onFinish={handleSizeSubmit}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="scode" label="S. Code">
              <Input placeholder="S. Code" />
            </Form.Item>
            <Form.Item name="cm" label="CM">
              <Input placeholder="CM" />
            </Form.Item>
            <Form.Item name="inches" label="Inches">
              <Input placeholder="Inches" />
            </Form.Item>
            <Form.Item name="size" label="Size">
              <Input placeholder="Size" />
            </Form.Item>
            <Form.Item name="gender" label="Gender">
              <Input placeholder="Gender" />
            </Form.Item>
            <Form.Item name="europe" label="Europe">
              <Input placeholder="Europe" />
            </Form.Item>
            <Form.Item name="usa" label="USA">
              <Input placeholder="USA" />
            </Form.Item>
            <Form.Item name="mexico" label="Mexico">
              <Input placeholder="Mexico" />
            </Form.Item>
            <Form.Item name="br-brasil" label="BR-Brasil">
              <Input placeholder="BR-Brasil" />
            </Form.Item>
            <Form.Item name="cn" label="CN">
              <Input placeholder="CN" />
            </Form.Item>
            <Form.Item name="lowercn" label="Lower CN">
              <Input placeholder="Lower CN" />
            </Form.Item>
            <Form.Item name="cnupperwoman" label="CN Upper Woman">
              <Input placeholder="CN Upper Woman" />
            </Form.Item>
            <Form.Item name="cnlowerwoman" label="CN Lower Woman">
              <Input placeholder="CN Lower Woman" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  )
}
